import { isFreedomToSpeakUpFeatureEnabled } from '@config/freedomToSpeakUpFeature';
import { useAppSelector } from '@hooks/useAppSelector';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import { CreateSpeakUp } from '../AssessmentScores/CreateSpeakUp/CreateSpeakUp';
import { FreedomToSpeakUpChooserModal } from '../FreedomToSpeakUp/FreedomToSpeakUpChooserModal';

type FlowPhase = 'off' | 'chooser' | 'classic';

export interface SpeakUpFlowLauncherProps {
  speakUpOpen: boolean;
  /** Called with `false` when the user dismisses the flow or finishes the classic Speak Up modal. */
  onSpeakUpOpenChange: (open: boolean) => void;
  assessmentId: number | null;
  questionnaireId: number | null;
}

/**
 * When Freedom to Speak Up is enabled for the company, opens the chooser first; otherwise opens
 * the existing CreateSpeakUp modal unchanged. CreateSpeakUp itself is not modified.
 */
export const SpeakUpFlowLauncher: React.FC<SpeakUpFlowLauncherProps> = ({
  speakUpOpen,
  onSpeakUpOpenChange,
  assessmentId,
  questionnaireId,
}) => {
  const history = useHistory();
  const companyConfig = useAppSelector((state: RootState) => state.companyConfig);
  const f2suEnabled = useMemo(
    () => isFreedomToSpeakUpFeatureEnabled(companyConfig.data?.freedomToSpeakUpEnabled),
    [companyConfig.data?.freedomToSpeakUpEnabled],
  );

  const [phase, setPhase] = useState<FlowPhase>('off');

  useLayoutEffect(() => {
    if (!speakUpOpen) {
      setPhase('off');

      return;
    }

    setPhase(f2suEnabled ? 'chooser' : 'classic');
  }, [speakUpOpen, f2suEnabled]);

  const dismissAll = () => {
    setPhase('off');
    onSpeakUpOpenChange(false);
  };

  const handleClassicModalOpen = (next?: boolean) => {
    if (next !== true) {
      setPhase('off');
      onSpeakUpOpenChange(false);
    }
  };

  if (!speakUpOpen) {
    return null;
  }

  return (
    <>
      <FreedomToSpeakUpChooserModal
        open={phase === 'chooser'}
        onClose={dismissAll}
        onSelectShareFeelings={() => {
          setPhase('classic');
        }}
        onSelectRaiseConcern={() => {
          dismissAll();
          history.push('/freedom-to-speak-up');
        }}
      />
      {phase === 'classic' ? (
        <CreateSpeakUp
          createSpeakUpOpen
          handleSpeakUpModalOpen={handleClassicModalOpen}
          assessmentId={assessmentId}
          questionnaireId={questionnaireId}
        />
      ) : null}
    </>
  );
};
